/**
 * Deciding what a portal page should say when a project does not appear.
 *
 * Pure on purpose, for the same reason as `taskFragment.ts`: the web test setup
 * is `node --test` with no DOM, so the decision has to be reachable without
 * rendering. What is left in the component is drawing.
 *
 * The bug this exists to prevent (QA-84): the portal collapsed every failure
 * into `if (!project)` and rendered "your access to it has ended". A 502, an
 * expired session and a dropped connection all became an accusation that the
 * client had been removed from a project they still have. An outage should not
 * read as a revocation.
 *
 * Two rules carry most of the weight.
 *
 * **A transport failure is not evidence about the project.** Anything that is
 * not an authoritative answer from the server resolves to `unreachable`, whose
 * remedy is "try again", never to `unavailable`, whose remedy is "ask someone
 * for access". Unclassified errors fail toward retry, so a status nobody has
 * seen yet cannot become a false accusation.
 *
 * **Withheld and nonexistent are one outcome, not two.** A project the viewer
 * may not read and a project that does not exist both resolve to `unavailable`.
 * Not two outcomes with matching copy -- one outcome, so the difference is never
 * computed and cannot be leaked by a later edit that reworks the wording. The
 * difference is absent at the source as well: Directus 11.17.4 answers 403
 * `FORBIDDEN` for a forbidden item, a well-formed id that matches no row, and a
 * malformed id alike, measured against the running instance on 2026-08-25. It
 * never answers 404 for an item, so a 404 branch here would be dead code whose
 * only possible effect is to mislead.
 */

/** What the caller's query knows so far. */
export type AccessInput = {
  /** Has the query produced an answer -- any answer -- yet? */
  settled: boolean;
  /** Whatever the query threw, if anything. */
  error: unknown;
  /** Does the caller hold a row to draw, including a stale cached one? */
  hasRow: boolean;
};

export type AccessOutcome =
  /** Draw the project. */
  | { kind: "ready" }
  /** No answer yet. Say nothing about the project; it is still in flight. */
  | { kind: "looking" }
  /** Authoritatively not for this viewer: withheld, or no such project. */
  | { kind: "unavailable" }
  /** The session is gone. Signing in again is the remedy. */
  | { kind: "signed-out" }
  /** We could not get an answer. The project's status is unknown. */
  | { kind: "unreachable" };

/**
 * HTTP status of a thrown request, when there is one.
 *
 * The `../providers/api` v22 REST client throws an object carrying the fetch
 * `Response`, so the status lives at `response.status` -- measured, not assumed.
 * A network failure throws a `TypeError` with no `response` at all, which is
 * exactly what makes "the server did not answer" distinguishable from "the
 * server answered no". `status` is also read directly because Refine and its
 * query layer re-wrap some errors.
 */
export function httpStatusOf(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  const e = error as { response?: { status?: unknown }; status?: unknown };
  const fromResponse = e.response?.status;
  if (typeof fromResponse === "number") return fromResponse;
  if (typeof e.status === "number") return e.status;
  return undefined;
}

export function resolvePortalAccess(input: AccessInput): AccessOutcome {
  const { settled, error, hasRow } = input;
  const status = httpStatusOf(error);

  // Answered by the server about this viewer's access. These are authoritative
  // even when a stale row is still in hand, because continuing to draw a
  // project after access was withdrawn is the failure that matters here.
  if (status === 401) return { kind: "signed-out" };
  if (status === 403) return { kind: "unavailable" };

  // A row we already have outranks a transport problem. Blanking out a project
  // someone is reading because a background refetch hit a 502 would be the same
  // bug as the one above, just briefer.
  if (hasRow) return { kind: "ready" };

  // Reached only when there is no row to fall back on. Every remaining error --
  // 5xx, a timeout, a refused connection, a status not yet seen -- means the
  // project's status is unknown, which is not the same as bad news about it.
  if (error) return { kind: "unreachable" };

  if (!settled) return { kind: "looking" };

  // Settled, no error, no row. Directus makes this hard to reach for a
  // fetch-by-id, but a filtered list read answers an unreadable row and a
  // missing row identically with an empty result, and that is this case.
  return { kind: "unavailable" };
}
